import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { RIDEBOOK_CHANNEL_LABELS, RIDEBOOK_KIND_LABELS, RIDEBOOK_PAYMENT_LABELS, RIDEBOOK_SOURCE_LABELS, RIDEBOOK_KINDS, type RidebookEntry, type RidebookKind, type RidebookOrderView } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { EntryForm } from './EntryForm';
import { entryLink } from './RidebookPage';
import { euro, summary, text, when } from './shared';
import './ridebook.css';

const STATE_LABELS: Record<RidebookOrderView['state'], string> = { received: 'Eingegangen', assigned: 'An den Fahrer weitergegeben', started: 'Unterwegs', finished: 'Ausgeführt', cancelled: 'Nicht ausgeführt' };

/** Kurzfassung der Chain-Daten eines bestätigten Eintrags */
function ChainInfo({ e }: { e: RidebookEntry }) {
  if (!e.hash) return null;
  return (
    <details className="ridebook-chain">
      <summary>Nachweis zur Unveränderlichkeit</summary>
      <dl className="ridebook-facts">
        <div><dt>Nummer in der Kette</dt><dd>{e.seq}</dd></div>
        <div><dt>Erfasst am</dt><dd>{when(e.finalizedAt)}{e.finalizedByName ? `, ${e.finalizedByName}` : e.source === 'auto' ? ', automatisch' : ''}</dd></div>
        <div><dt>Hash</dt><dd><code className="ridebook-hash">{e.hash}</code></dd></div>
        <div><dt>Hash des Vorgängers</dt><dd><code className="ridebook-hash">{e.prevHash}</code></dd></div>
        <div><dt>Siegel des Servers</dt><dd>{e.sealed ? 'vorhanden' : 'nicht vorhanden'}</dd></div>
      </dl>
    </details>
  );
}

function Details({ e }: { e: RidebookEntry }) {
  const p = e.payload;
  const rows: [string, string][] = [];
  const add = (label: string, value: string) => value && rows.push([label, value]);
  if (e.kind === 'received') {
    add('Auftrag eingegangen', when(p.receivedAt));
    add('Eingegangen über', RIDEBOOK_CHANNEL_LABELS[p.receivedVia as keyof typeof RIDEBOOK_CHANNEL_LABELS] ?? text(p.receivedVia));
    add('Fahrgast', text(p.passengerName));
    add('Telefon', text(p.passengerPhone));
    add('Abholort', text(p.pickupAddress));
    add('Zielort', text(p.destinationAddress));
    add('Beginn laut Auftrag', when(p.pickupAt));
    add('Fahrgäste', p.passengers ? String(p.passengers) : '');
    add('Beauftragte Strecke', typeof p.orderedKm === 'number' ? `${p.orderedKm.toLocaleString('de-DE')} km` : '');
    add('Vereinbarter Preis', typeof p.agreedPriceCents === 'number' ? euro(p.agreedPriceCents) : '');
    add('Anfrage eingegangen', p.requestReceivedAt ? when(p.requestReceivedAt) : '');
    add('Bemerkung', text(p.note));
  } else if (e.kind === 'assigned') {
    add('Weitergegeben am', when(p.assignedAt));
    add('Kennzeichen', text(p.plate));
    add('Fahrer', text(p.driverName));
    add('Standort des Fahrzeugs', text(p.vehiclePosition));
  } else if (e.kind === 'started') {
    add('Fahrtbeginn', when(p.startedAt));
    add('Ort', text(p.startPlace));
    add('Kilometerstand', typeof p.odometerKm === 'number' ? String(p.odometerKm) : '');
  } else if (e.kind === 'finished') {
    add('Fahrtende', when(p.finishedAt));
    add('Ort', text(p.endPlace));
    add('Gefahrene Strecke', typeof p.distanceKm === 'number' ? `${p.distanceKm.toLocaleString('de-DE')} km` : '');
    add('Kilometerstand', typeof p.odometerKm === 'number' ? String(p.odometerKm) : '');
    add('Fahrpreis', typeof p.priceCents === 'number' ? euro(p.priceCents) : '');
    add('Zahlungsart', RIDEBOOK_PAYMENT_LABELS[p.paymentMethod as keyof typeof RIDEBOOK_PAYMENT_LABELS] ?? '');
  } else if (e.kind === 'cancelled') {
    add('Zeitpunkt', when(p.cancelledAt));
    add('Grund', text(p.reason));
  } else add('Berichtigung', text(p.text));
  return (
    <dl className="ridebook-facts">
      {rows.map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  );
}

/** Ein Auftrag mit allen seinen Einträgen in der Reihenfolge der Kette. Ergänzungen entstehen als neue Einträge, nie als Änderung. */
export function RidebookOrderPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const view = useQuery({ queryKey: ['ridebook', 'order', id], queryFn: async () => (await api<{ data: RidebookOrderView }>('GET', `/ridebook/orders/${id}`)).data });
  if (view.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!view.data) return <div className="alert alert--error" role="alert">{view.error instanceof ApiError ? view.error.message : 'Der Auftrag konnte nicht geladen werden.'}</div>;
  const { received, events, state } = view.data;
  const finalEvents = events.filter((e) => e.status === 'final');
  const drafts = events.filter((e) => e.status === 'draft' && e.id !== received.id);
  const addable = RIDEBOOK_KINDS.filter((k) => k !== 'received');

  return (
    <>
      <div className="page-head">
        <h1>Auftrag {received.orderNo ?? ''}{received.status === 'draft' ? ' (Entwurf)' : ''}</h1>
        <span className="muted">{STATE_LABELS[state]}</span>
      </div>
      <p className="muted"><Link to="/auftragsbuch">← Auftragsbuch</Link>{received.orderNumber && <> · Auftrag in Wirkia: {received.orderNumber}</>}</p>

      {received.status === 'draft' ? (
        <div className="alert alert--warn" role="note">Dieser Auftragseingang ist noch ein Entwurf. <Link to={`/auftragsbuch/entwurf/${received.id}`}>Entwurf bearbeiten und eintragen</Link></div>
      ) : (
        <>
          <section className="card">
            <h2>{RIDEBOOK_KIND_LABELS.received}</h2>
            <p className="muted small">{RIDEBOOK_SOURCE_LABELS[received.source]}, Nr. {received.seq}{received.lateMinutes !== null ? ', nachträglich erfasst' : ''}</p>
            <Details e={received} />
            <ChainInfo e={received} />
          </section>

          {finalEvents.filter((e) => e.id !== received.id).map((e) => (
            <section key={e.id} className="card">
              <h2>{RIDEBOOK_KIND_LABELS[e.kind]}</h2>
              <p className="muted small">{RIDEBOOK_SOURCE_LABELS[e.source]}, Nr. {e.seq}{e.lateMinutes !== null ? ', nachträglich erfasst' : ''}</p>
              <Details e={e} />
              <ChainInfo e={e} />
            </section>
          ))}

          {drafts.length > 0 && (
            <section className="card">
              <h2>Entwürfe zu diesem Auftrag</h2>
              <ul className="plain-list">{drafts.map((d) => <li key={d.id}><Link to={entryLink(d)}>{RIDEBOOK_KIND_LABELS[d.kind]}</Link>: {summary(d)}</li>)}</ul>
            </section>
          )}

          {can('ridebook.write') && (
            <section className="card">
              <h2>Eintrag hinzufügen</h2>
              <p className="muted small">Im Auftragsbuch ändert sich nichts nachträglich. Was danach geschieht, kommt als neuer Eintrag dazu. Wirkia trägt Weitergabe, Fahrtbeginn und Fahrtende selbst ein, wenn Sie Fahrzeug und Fahrer einplanen und den Auftrag voranschalten. Hier tragen Sie nach, was dort nicht entstand, oder berichtigen.</p>
              <div className="form-actions">
                {addable.map((k) => <Link key={k} className="btn btn--secondary btn--sm" to={`/auftragsbuch/neu?art=${k}&auftrag=${received.id}`}>{RIDEBOOK_KIND_LABELS[k]}</Link>)}
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}

/** Neuer Entwurf. Ohne Angabe ein Auftragseingang, mit `art` und `auftrag` ein Eintrag zu einem bestehenden Auftrag. */
export function RidebookNewPage() {
  const [params] = useSearchParams();
  const kind = (RIDEBOOK_KINDS as readonly string[]).includes(params.get('art') ?? '') ? (params.get('art') as RidebookKind) : 'received';
  const refId = params.get('auftrag') ?? undefined;
  return (
    <>
      <div className="page-head"><h1>Eintrag ins Auftragsbuch</h1></div>
      <p className="muted"><Link to={refId ? `/auftragsbuch/${refId}` : '/auftragsbuch'}>← Zurück</Link></p>
      <EntryForm key={`${kind}-${refId ?? ''}`} kind={kind} refId={refId} />
    </>
  );
}

/** Entwurf bearbeiten (auch die Entwürfe, die Wirkia aus Aufträgen angelegt hat) */
export function RidebookDraftPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const entry = useQuery({ queryKey: ['ridebook', 'entry', id], queryFn: async () => (await api<{ data: RidebookEntry }>('GET', `/ridebook/entries/${id}`)).data });
  if (entry.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!entry.data) return <div className="alert alert--error" role="alert">Der Entwurf wurde nicht gefunden.</div>;
  const e = entry.data;
  if (e.status === 'final') {
    navigate(entryLink(e), { replace: true });
    return null;
  }
  return (
    <>
      <div className="page-head"><h1>Entwurf: {RIDEBOOK_KIND_LABELS[e.kind]}</h1></div>
      <p className="muted"><Link to={e.refId ? `/auftragsbuch/${e.refId}` : '/auftragsbuch?tab=drafts'}>← Zurück</Link></p>
      <EntryForm key={e.id} kind={e.kind} refId={e.refId ?? undefined} entry={e} />
    </>
  );
}
